import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Currency, MultipleCarsPerUserTreatAs, ParkingSubscriptionType, ParkingSubscriptionUsage, ParkingSubscriptionPaidBy, ParkingQuotaDuration, IParkerCompanyDTO, IParkingSubscriptionLimitedDTO } from 'src/app/base/models/models.swagger.generated';
import { CurrencyCents } from './currency-cents';
import { TranslateModule } from '@ngx-translate/core';
import { NumericTextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { NgClass } from '@angular/common';
import { MultiFieldDurationPickerComponent } from '../../../../../../base/shared/multi-field-duration-picker/multi-field-duration-picker.component';
import { ButtonGroupComponent } from '../../../../../../base/shared/button-group/button-group.component';
import { YesNoGroupButtonComponent } from '../../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { AppErrorMessagesComponent } from '../../../../../../base/error-messages/app-error-messages.component';
import { SubscriptionEditFormGroup } from './subscription-details-general-form.type';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { CompanyService } from 'src/app/base/services/company.service';
import { DropDownConstantsFactory } from '../../../../../../base/shared/syncfusion/dropdown-constants-factory';
import { Subscription } from 'rxjs';
import { SubscriptionDetailsService } from '../subscription-details.service';
import { AuthService } from 'src/app/base/services/auth.service';

export type MaxParkingDurationInMinFormType = {
  days: FormControl<number>;
  hours: FormControl<number>;
  minutes: FormControl<number>;
};

@Component({
  selector: 'app-subscription-details-general',
  templateUrl: './subscription-details-general.component.html',
  styleUrls: ['./subscription-details-general.component.scss'],
  imports: [ReactiveFormsModule, AppErrorMessagesComponent, YesNoGroupButtonComponent, ButtonGroupComponent, MultiFieldDurationPickerComponent, NgClass, NumericTextBoxModule, TranslateModule, DropDownListModule],
  providers: [CompanyService],
})
export class SubscriptionDetailsGeneralComponent implements OnInit, OnDestroy {
  @Input()
  public subscriptionForm: SubscriptionEditFormGroup;

  public CurrencyEnum = Currency;
  public CurrencyCentsEnum = CurrencyCents;
  public ParkingSubscriptionType = ParkingSubscriptionType;
  public MultipleCarsPerUserTreatAs = MultipleCarsPerUserTreatAs;
  public Usage = ParkingSubscriptionUsage;
  public PaidBy = ParkingSubscriptionPaidBy;
  public QuotaDuration = ParkingQuotaDuration;

  public companies: IParkerCompanyDTO[] = [];
  public fallbackSubscriptions: IParkingSubscriptionLimitedDTO[] = [];
  public isLoading = false;

  public get isVisitorSubscription(): boolean {
    return this.subscriptionForm.controls.type.getRawValue() === ParkingSubscriptionType.ForeignParkerSubscription
          && (this.subscriptionForm.controls.usage.getRawValue() === ParkingSubscriptionUsage.Visitor
          || this.subscriptionForm.controls.usage.getRawValue() === ParkingSubscriptionUsage.VisitorAndParkerAsVisitor);
  }

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();

  private subscription: Subscription = new Subscription();
  private _subscriptionId: number | null;

  constructor(private companyService: CompanyService,
              private subscriptionService: SubscriptionDetailsService,
              private authService: AuthService,) {
  }

  ngOnInit() {
    this.getFallbackSubscriptions();
    this.initCompanyField();
    this.subscription.add(this.subscriptionForm.controls.id.valueChanges.subscribe((id) => {
      if (this._subscriptionId !== id) {
        this._subscriptionId = id;
        this.getFallbackSubscriptions();
        this.initCompanyField();
      }
    }));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private initCompanyField(): void {
    const loggedInUser = this.authService.getUserInfo();
    if (this.authService.isUserCompanyAdmin()) {
      this.companies = [loggedInUser.company];
      this.subscriptionForm.controls.companyId.setValue(loggedInUser.companyId);
      this.subscriptionForm.controls.companyId.disable();
    } else {
      this.getCompanies();
      this.subscriptionForm.controls.companyId.enable();
    }
  }

  private getCompanies(): void {
    if (this.subscriptionForm.controls.clientId.getRawValue() != null) {
      this.companyService.getCompanies(this.subscriptionForm.controls.clientId.getRawValue())
        .subscribe(companies => {
          this.companies = companies;
          // Re-set the value to trigger selection in the dropdown
          const currentValue = this.subscriptionForm.controls.companyId.value;
          if (currentValue != null) {
            this.subscriptionForm.controls.companyId.setValue(currentValue);
          }
        });
    } else {
      this.companies = [];
    }
  }

  private getFallbackSubscriptions(): void {
    this.fallbackSubscriptions = [];
    this.isLoading = true;
    if (this.subscriptionForm.controls.clientId.getRawValue() != null && this.subscriptionForm.controls.id.getRawValue() != null) {
      this.subscriptionService.getSubscriptionsForValidationFallback(this.subscriptionForm.controls.clientId.getRawValue(), this.subscriptionForm.controls.id.getRawValue()).subscribe(subscriptions => {
        this.fallbackSubscriptions = subscriptions;
        this.isLoading = false;
      });
    } else {
      this.isLoading = false;
    }
  }
}
